'use client';

import { useMemo, useRef } from 'react';

import { useCheckSpotOnly } from '@flipster/features/src/shared/regulation';
import { Video } from '@flipster/react/design-utils/video';
import { css } from '@flipster/styled-system/css';
import { center, grid } from '@flipster/styled-system/patterns';
import { isTradingFeeFeatureEnabled } from '@flipster/utils/feature-flag';
import { ProdStaticPath } from '@flipster/utils/url';

import {
  maxWStylesWithMargin,
  sectionLargeHeadingStyles,
  sectionSubHeadingStyles,
} from '@web/app/[locale]/(web)/_main-page/_styles';
import { useMainPageIntersectionSegment } from '@web/app/[locale]/(web)/_main-page/hooks/useIntersectionSegment';
import design from '@web/assets/images/main-page/design.webp';
import fast from '@web/assets/images/main-page/fast.webp';
import liquidity from '@web/assets/images/main-page/liquidity.webp';
import zeroFee from '@web/assets/images/main-page/zero-fee.webp';
import earn from '@web/assets/images/shared/earn.webp';
import fast2 from '@web/assets/images/shared/fast.webp';
import interfaceIcon from '@web/assets/images/shared/interface.webp';
import security from '@web/assets/images/shared/security.webp';
import trading from '@web/assets/images/shared/trading.webp';
import zeroSpread from '@web/assets/images/shared/zero-spread.webp';

const Designed = () => {
  const intersectionRef = useRef<HTMLHeadingElement>(null);
  const isSpotOnly = useCheckSpotOnly();
  useMainPageIntersectionSegment(
    intersectionRef,
    isTradingFeeFeatureEnabled
      ? isSpotOnly
        ? 'Simple. Secure. Easy to Use.'
        : 'Made for speed and precision'
      : 'Designed for fast trade on-the-go',
  );

  const contents = useMemo(() => {
    if (!isTradingFeeFeatureEnabled) {
      return {
        title: 'Designed for fast \ntrade on-the-go',
        desc: 'Get the best opportunities at minimal cost',
        items: [
          {
            imgSrc: fast.src,
            title: 'Lightning-fast listing',
            desc: "Gain first access to exclusive trading opportunities with the world's no. 1 platform in offering lightning-fast listings on the hottest cryptocurrencies.",
          },
          {
            imgSrc: zeroFee.src,
            title: 'Trading Fee',
            desc: isSpotOnly
              ? 'Enjoy zero trading fees when you trade on Flipster, Save and earn more regardless of whether you’re a flipper or HODLer.'
              : 'Enjoy zero trading fees when you trade on Flipster, for more rewards as you flip. Save and earn more regardless of whether you’re a flipper or HODLer.',
          },
          {
            imgSrc: liquidity.src,
            title: 'High liquidity',
            desc: 'Flipster has partnered with a professional designated market maker that provides our platform with unparalleled deep liquidity. Trade with ease at the best bid and offer prices.',
          },
        ],
      };
    }

    if (isSpotOnly) {
      return {
        title: 'Simple. Secure. \nEasy to Use.',
        items: [
          {
            imgSrc: trading.src,
            title: 'Simple, intuitive trading',
            desc: 'Buy and sell crypto in just a few taps. Designed for ease, not overload.',
          },
          {
            imgSrc: security.src,
            title: 'Security you can trust',
            desc: 'From sign-up to every trade, you’re protected by best-in-class security and full regulatory compliance.',
          },
          {
            imgSrc: interfaceIcon.src,
            title: 'Clarity you can count on',
            desc: `A clean, focused interface that puts you in control, whether you're starting out or scaling up.`,
          },
        ],
      };
    }

    return {
      title: 'Made for speed and \nprecision',
      items: [
        {
          imgSrc: fast2.src,
          title: 'Move fast. Trade faster',
          desc: 'Get filled instantly with lightning-fast execution and deep liquidity.',
        },
        {
          imgSrc: zeroSpread.src,
          title: 'Zero spreads',
          desc: 'Enjoy no spreads for major pairs with precision pricing that moves with you.',
        },
        {
          imgSrc: earn.src,
          title: 'Seamless earn',
          desc: 'Maximize capital efficiency by earning on USDT even as you trade.',
        },
      ],
    };
  }, [isSpotOnly]);

  return (
    <section className={css(maxWStylesWithMargin)}>
      <h2 ref={intersectionRef} className={css(sectionLargeHeadingStyles, isTradingFeeFeatureEnabled && { mb: 0 })}>
        {contents.title}
      </h2>
      {contents.desc && <p className={css(sectionSubHeadingStyles)}>{contents.desc}</p>}
      <div className={center()}>
        {isTradingFeeFeatureEnabled && (
          <Video
            poster={{
              base: `${ProdStaticPath}/images/sub-hero-mb.webp`,
              tablet: `${ProdStaticPath}/images/sub-hero-dt.webp`,
            }}
            src={{
              base: `${ProdStaticPath}/videos/sub-hero-mb.mp4`,
              tablet: `${ProdStaticPath}/videos/sub-hero-dt.mp4`,
            }}
            css={{
              w: '100%',
              minW: { tablet: 724 },
              minH: { base: 250, tablet: 730 },
              mb: { base: '-10%', tablet: '-22%' },
              zIndex: -1,
            }}
            lazy
          />
        )}
        {!isTradingFeeFeatureEnabled && (
          <img
            src={design.src}
            alt='designed flipster logo'
            width={1000}
            height={450}
            className={css({
              h: 'auto',
              w: '100%',
              maxW: 1080,
              mb: '-10%',
            })}
            loading='lazy'
          />
        )}
      </div>
      <ul className={grid({ gap: 16, columns: { base: 1, tablet: 3 } })}>
        {contents.items.map((content) => (
          <li
            className={css({
              p: 32,
              bgColor: 'background.default.level-1',
              rounded: 16,
              color: 'content.default.level-4',
            })}
            key={content.title}
          >
            <img
              src={content.imgSrc}
              alt={content.title}
              className={css({
                h: { base: 50, tablet: 70 },
                w: { base: 50, tablet: 70 },
                mb: { base: 8, tablet: 24 },
              })}
            />
            <h3 className={css({ textStyle: 'Title/M', mb: 16 })}>{content.title}</h3>
            <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3' })}>{content.desc}</p>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default Designed;
